import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, ChevronLeft, ChevronRight } from 'lucide-react';

const MensHealth = () => {
  const navigate = useNavigate();
  const [currentBenefitIndex, setCurrentBenefitIndex] = useState(0);

  const scrollToContact = () => {
    navigate('/');
    setTimeout(() => {
      const element = document.getElementById('contact');
      if (element) {
        const offset = 90;
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - offset;
        
        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
      }
    }, 100);
  };

  const pricingOptions = [
    {
      title: 'Testosterone Replacement',
      price: 'Dose Dependent',
      unit: ''
    },
    {
      title: 'Apex RF for Erectile Dysfunction',
      price: '$500',
      unit: 'per session (Recommended 6 sessions)'
    }
  ];

  const benefits = [
    {
      title: 'Increases Energy & Vitality',
      description: 'Restores natural energy levels, reducing fatigue and improving overall vitality and stamina.'
    },
    {
      title: 'Improves Muscle Mass & Strength',
      description: 'Supports lean muscle development and strength gains when combined with proper exercise.'
    },
    {
      title: 'Enhances Sexual Function',
      description: 'Improves libido, sexual performance, and addresses erectile dysfunction concerns.'
    },
    {
      title: 'Boosts Mental Clarity & Focus',
      description: 'Enhances cognitive function, concentration, and mental sharpness.'
    },
    {
      title: 'Supports Bone Density',
      description: 'Maintains and improves bone strength, reducing risk of osteoporosis and fractures.'
    },
    {
      title: 'Reduces Body Fat',
      description: 'Helps decrease body fat percentage, particularly around the midsection.'
    },
    {
      title: 'Improves Mood & Motivation',
      description: 'Alleviates symptoms of depression, irritability, and low motivation associated with low testosterone.'
    },
    {
      title: 'Enhances Sleep Quality',
      description: 'Promotes better sleep patterns and more restful, restorative sleep.'
    },
    {
      title: 'Cardiovascular Health Benefits',
      description: 'May improve heart health markers and overall cardiovascular function when properly managed.'
    },
    {
      title: 'Non-Invasive RF Treatment',
      description: 'Apex RF offers a non-surgical solution for erectile dysfunction with minimal downtime.'
    },
    {
      title: 'Personalized Treatment Plans',
      description: 'Customized hormone replacement protocols tailored to your individual needs and health goals.'
    },
    {
      title: 'Ongoing Medical Supervision',
      description: 'Regular monitoring and adjustments ensure optimal results and safety throughout treatment.'
    }
  ];

  return (
    <>
      <Helmet>
        <title>Men's Health - Testosterone & ED Treatment | Century Aesthetics</title>
        <meta name="description" content="Comprehensive men's health services including testosterone replacement therapy and Apex RF treatment for ED in Forest Hills, NY. Confidential, expert care." />
        <meta name="keywords" content="men's health, testosterone therapy, ED treatment, erectile dysfunction, TRT, men's wellness, hormone therapy" />
        <link rel="canonical" href="https://www.centuryaesthetics.org/mens-health" />
        
        <meta property="og:title" content="Men's Health Services | Century Longevity & Aesthetics" />
        <meta property="og:description" content="Comprehensive men's health services including testosterone replacement therapy and Apex RF treatment for ED in Forest Hills, NY. Confidential, expert " />
        <meta property="og:url" content="https://www.centuryaesthetics.org/mens-health" />
      </Helmet>

      <div className="min-h-screen bg-white pt-28">

      {/* Main Content Section */}
      <section className="py-12">
        <div className="max-w-[1400px] mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative"
            >
              <div className="rounded-lg overflow-hidden shadow-2xl h-[500px]">
                <img 
                  src="/mensconsult.jpg" 
                  alt="Men's Health - Testosterone Replacement & Apex RF" 
                  className="w-full h-full object-cover"
                />
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-4xl font-heading font-bold text-olive-darkest mb-6">
                MEN'S HEALTH: TESTOSTERONE REPLACEMENT & APEX RF THERAPY
              </h2>
              <div className="w-20 h-1 bg-gold mb-6"></div>
              
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>
                  Our comprehensive men's health program addresses low testosterone and erectile dysfunction through evidence-based medical treatments. Testosterone Replacement Therapy (TRT) restores optimal hormone levels to improve energy, muscle mass, libido, mental clarity, and overall vitality. Under physician supervision, personalized TRT protocols are tailored to your individual needs with ongoing monitoring to ensure safe, effective results. Treatment options include injections, gels, or pellets depending on your preference and lifestyle.
                </p>
                <p>
                  Apex RF therapy offers a non-invasive solution for erectile dysfunction using advanced radiofrequency technology to improve blood flow and tissue regeneration. Most patients achieve optimal results with 6 sessions. Both treatments are performed in a comfortable, confidential setting with complete medical oversight. Regular follow-up appointments track progress and adjust treatment as needed. Schedule your confidential consultation at Century Longevity & Aesthetics to reclaim your vitality and performance.
                </p>
              </div>

              <motion.button
                whileHover={{ y: -2 }}
                whileTap={{ scale: 0.98 }}
                onClick={scrollToContact}
                className="mt-8 bg-olive-darkest text-white px-8 py-4 rounded font-medium text-lg tracking-wide hover:bg-gold hover:text-olive-darkest transition-all duration-300 shadow-lg hover:shadow-xl"
              >
                Book Now
              </motion.button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-12 bg-olive-darkest">
        <div className="max-w-[1400px] mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl font-heading font-bold text-white mb-4">Men's Health Pricing</h2>
            <div className="w-20 h-1 bg-gold mx-auto mb-6"></div>
            <div className="inline-flex items-center gap-3 bg-gold/10 border-2 border-gold px-8 py-4 rounded-lg">
              <CheckCircle className="w-6 h-6 text-gold" />
              <p className="text-white text-lg font-medium">
                Free Consultation for Men's Health Services
              </p>
            </div>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {pricingOptions.map((option, index) => (
              <motion.div
                key={option.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-white rounded-lg p-10 shadow-xl text-center"
              >
                <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-gold/10 flex items-center justify-center">
                  <svg 
                    className="w-10 h-10 text-gold" 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path 
                      strokeLinecap="round" 
                      strokeLinejoin="round" 
                      strokeWidth={1.5} 
                      d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" 
                    />
                  </svg>
                </div>
                
                <h3 className="text-xl font-heading font-semibold text-olive-darkest mb-4 uppercase tracking-wide min-h-[3rem] flex items-center justify-center">
                  {option.title}
                </h3>
                <p className="text-4xl font-heading font-light text-gold mb-2">
                  {option.price}
                </p>
                {option.unit && (
                  <p className="text-sm font-body text-gray-600 mb-6">
                    {option.unit}
                  </p>
                )}
                
                <motion.button
                  whileHover={{ y: -2 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={scrollToContact}
                  className="w-full bg-olive-darkest text-white px-6 py-3 rounded-lg font-medium text-sm tracking-wide hover:bg-gold hover:text-olive-darkest transition-all duration-300 shadow-lg"
                >
                  Book Now
                </motion.button>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-[1400px] mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl font-heading font-bold text-olive-darkest mb-4">Benefits of Men's Health Treatments</h2>
            <div className="w-20 h-1 bg-gold mx-auto"></div>
          </motion.div>

          <div className="max-w-4xl mx-auto">
            {/* Carousel Container */}
            <div className="relative">
              {/* Previous Button */}
              <button
                onClick={() => setCurrentBenefitIndex((prev) => (prev === 0 ? benefits.length - 1 : prev - 1))}
                className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 sm:-translate-x-12 z-10 bg-white rounded-full p-3 shadow-lg hover:bg-gold hover:text-white transition-all duration-300"
                aria-label="Previous benefit"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>

              {/* Next Button */}
              <button
                onClick={() => setCurrentBenefitIndex((prev) => (prev === benefits.length - 1 ? 0 : prev + 1))}
                className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 sm:translate-x-12 z-10 bg-white rounded-full p-3 shadow-lg hover:bg-gold hover:text-white transition-all duration-300"
                aria-label="Next benefit"
              >
                <ChevronRight className="w-6 h-6" />
              </button>

              {/* Carousel Card */}
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentBenefitIndex}
                  initial={{ opacity: 0, x: 100 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -100 }}
                  transition={{ duration: 0.3 }}
                  className="bg-white rounded-lg shadow-xl p-8 sm:p-12 min-h-[300px] flex flex-col justify-center"
                >
                  <div className="flex items-start gap-6">
                    <div className="flex-shrink-0">
                      <CheckCircle className="w-16 h-16 text-gold" strokeWidth={1.5} />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-heading text-3xl font-semibold text-olive-darkest mb-4">
                        {benefits[currentBenefitIndex].title}
                      </h3>
                      <p className="text-lg text-gray-700 leading-relaxed">
                        {benefits[currentBenefitIndex].description}
                      </p>
                    </div>
                  </div>
                </motion.div>
              </AnimatePresence>

              {/* Dots Indicator */}
              <div className="flex justify-center gap-2 mt-8">
                {benefits.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentBenefitIndex(index)}
                    className={`h-2 rounded-full transition-all duration-300 ${
                      index === currentBenefitIndex 
                        ? 'w-8 bg-gold' 
                        : 'w-2 bg-gray-300 hover:bg-gold/50'
                    }`}
                    aria-label={`Go to benefit ${index + 1}`}
                  />
                ))}
              </div>
            </div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="mt-16 bg-olive-darkest text-white p-8 sm:p-10 md:p-12 rounded-xl shadow-2xl text-center"
          >
            <p className="text-base sm:text-lg leading-relaxed mb-8 text-white">
              Reclaim your vitality, strength, and confidence with our comprehensive men's health treatments!
            </p>
            <motion.button
              whileHover={{ y: -2, scale: 1.05 }}
              whileTap={{ scale: 0.98 }}
              onClick={scrollToContact}
              className="w-full sm:w-auto bg-gold text-olive-darkest px-8 sm:px-10 md:px-12 py-3 sm:py-4 rounded-lg font-semibold text-base sm:text-lg tracking-wide hover:bg-white transition-all duration-300 shadow-lg hover:shadow-xl touch-manipulation mb-10"
            >
              Schedule Consultation
            </motion.button>

            <div className="w-24 h-0.5 bg-gold/30 mx-auto mb-8"></div>

            <h3 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold mb-4 text-white">Have Questions?</h3>
            <p className="text-base sm:text-lg text-white mb-6 sm:mb-8 max-w-2xl mx-auto leading-relaxed">
              Learn about men's health treatments, what to expect, and how we can help you achieve optimal health and performance.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <motion.button
                whileHover={{ y: -2, scale: 1.05 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => {
                  navigate('/faq');
                  setTimeout(() => {
                    const element = document.getElementById('mens-health');
                    if (element) {
                      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }
                  }, 100);
                }}
                className="w-full sm:w-auto bg-white/10 text-white border-2 border-white px-8 sm:px-10 md:px-12 py-3 sm:py-4 rounded-lg font-semibold text-base sm:text-lg tracking-wide hover:bg-white hover:text-olive-darkest transition-all duration-300 shadow-lg hover:shadow-xl touch-manipulation"
              >
                View FAQs
              </motion.button>
              <motion.button
                whileHover={{ y: -2, scale: 1.05 }}
                whileTap={{ scale: 0.98 }}
                onClick={scrollToContact}
                className="w-full sm:w-auto bg-white/10 text-white border-2 border-gold px-8 sm:px-10 md:px-12 py-3 sm:py-4 rounded-lg font-semibold text-base sm:text-lg tracking-wide hover:bg-gold hover:text-olive-darkest transition-all duration-300 shadow-lg hover:shadow-xl touch-manipulation"
              >
                Contact Us Directly
              </motion.button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
    </>
  );
};

export default MensHealth;